import type { DocumentNode } from 'graphql'
import { action, comparer, makeObservable, observable } from 'mobx'
import { computedFn } from 'mobx-utils'

import type { ILocationBase } from '~/client/graph'
import { DeleteLocationsDocument } from '~/client/graph/operations/generated/LocationType.generated'
import {
  ArchiveResourcePropertyDocument,
  BulkEditResourcePropertiesDocument,
  CreateResourcePropertyDocument,
  CreateResourceTemplateDocument,
  DeleteManyResourceTemplatesDocument,
  DeleteResourcePropertyDocument,
  GetResourcePropertiesDocument,
  GetResourceTemplatesDocument,
  UnarchiveResourcePropertyDocument,
  UpdateResourcePropertyDocument,
  UpdateResourceTemplateDocument,
} from '~/client/graph/operations/generated/Resources.generated'
import type { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type {
  ProjectResourceFailure,
  ProjectResourceOperation,
  ProjectResourceOperationFailure,
  ProjectResourceType,
  ResourceSaveCommand,
} from '~/client/src/shared/resources/domain/projectResourceType'
import type {
  IResourceControlCommands,
  IWorkflowRestrictionDelta,
} from '~/client/src/shared/resources/domain/resourceControls'
import type { ResourceResult } from '~/client/src/shared/resources/domain/resourceResult'
import {
  resourceErr,
  resourceOk,
} from '~/client/src/shared/resources/domain/resourceResult'
import type {
  CreateResourceFormInput,
  CreateResourceFormOutcome,
  CreateResourcePropertyInput,
  ResourcePropertiesState,
  ResourcePropertyDto,
  ResourceSchemaData,
  ResourceTemplateDto,
  UpdateResourceFormInput,
  UpdateResourcePropertyInput,
} from '~/client/src/shared/resources/domain/resourceSchema'

import type GraphExecutorStore from './GraphExecutor.store'
import ProjectResourceControls from './ProjectResourceControls'

type RemoteData<T> =
  | { readonly _tag: 'NotAsked' }
  | { readonly _tag: 'Loading'; readonly data: T | null }
  | { readonly _tag: 'Ready'; readonly data: T }
  | { readonly _tag: 'Failed'; readonly data: T | null; readonly error: string }

interface ResourceCollection<TResource> {
  /** Live records — what every list, picker, and report renders. */
  readonly records: IResourceRecord[]
  /** Live and soft-deleted records, index-aligned with `resources`. */
  readonly allRecords: IResourceRecord[]
  readonly resources: readonly TResource[]
}

const collectionOf = <TResource>(
  resources: readonly TResource[],
  allRecords: IResourceRecord[],
): ResourceCollection<TResource> => ({
  resources,
  allRecords,
  records: allRecords.filter(record => !record.isDeleted),
})

interface SchemaLoad {
  readonly projectId: string
  readonly generation: number
  readonly promise: Promise<
    ResourceResult<void, ProjectResourceOperationFailure>
  >
}

interface ResourcePropertiesData {
  readonly properties: ResourcePropertyDto[]
  readonly allProperties: ResourcePropertyDto[]
}

interface SchemaCommandContext {
  readonly ownerProjectId: string | null
  readonly generation: number
}

/** Observable projection exposed to resource consumers. */
export interface ProjectResourceView {
  readonly records: IResourceRecord[]
  /**
   * `records` plus soft-deleted ones, mirroring `listWithDeletedItems` on the
   * location stores. Only surfaces resolving a saved id to its last known name
   * should read this — deleted resources must stay unselectable everywhere else.
   */
  readonly recordsWithDeletedItems: IResourceRecord[]
  readonly loading: boolean
  readonly error: string | null
  readonly isReady: boolean
  readonly schema: {
    readonly templates: ResourceTemplateDto[]
    readonly properties: ResourcePropertyDto[]
    readonly allProperties: ResourcePropertyDto[]
    readonly loading: boolean
    readonly error: string | null
    readonly isReady: boolean
    /**
     * Readiness of the properties half alone. Consumers that render from properties —
     * cards, tables — should gate on these; the combined fields above stay conservative
     * for everything that genuinely needs templates.
     */
    readonly propertiesLoading: boolean
    readonly propertiesError: string | null
    readonly propertiesReady: boolean
  }
}

const EMPTY_RECORDS: IResourceRecord[] = []
const EMPTY_TEMPLATES: ResourceTemplateDto[] = []
const EMPTY_PROPERTIES: ResourcePropertyDto[] = []

interface WorkflowRestrictionReference {
  readonly id: string
  readonly item: object
}

const workflowRestrictionReferencesOf = (
  record: IResourceRecord,
): WorkflowRestrictionReference[] => {
  const restrictions: unknown = record.root?.workflowRestrictions
  if (!restrictions || typeof restrictions !== 'object') return []

  const groups = [
    'maxBookingDurations' in restrictions
      ? restrictions.maxBookingDurations
      : undefined,
    'maxOverlappingBookings' in restrictions
      ? restrictions.maxOverlappingBookings
      : undefined,
  ]
  return groups.flatMap(group => {
    if (!Array.isArray(group)) return []
    return group.flatMap((item: unknown) => {
      if (!item || typeof item !== 'object' || !('id' in item)) return []
      return typeof item.id === 'string' ? [{ id: item.id, item }] : []
    })
  })
}

// Every save mutation answers with the saved records under its one root field.
const savedResourceId = (data: object | undefined): string | null => {
  const [saved] = Object.values(data ?? {}) as Array<ReadonlyArray<{
    readonly id?: string | null
  } | null> | null>
  return saved?.[0]?.id ?? null
}

const dataOf = <T>(state: RemoteData<T>): T | null =>
  state._tag === 'NotAsked' ? null : state.data

const messageOf = (cause: unknown): string => {
  if (cause instanceof Error) return cause.message
  if (cause && typeof cause === 'object' && '_tag' in cause) {
    return String(cause._tag)
  }
  return 'Resource operation failed'
}

/**
 * Owns project-scoped records, schema, and commands for one resource type.
 *
 * @template TResource Lossless type-specific resource retained for edits.
 * @template TDomainFailure Type-specific decoding or command failure.
 */
export default class ProjectResourcesStore<TResource, TDomainFailure> {
  @observable.ref private recordState: RemoteData<
    ResourceCollection<TResource>
  > = { _tag: 'NotAsked' }
  // Templates and properties are fetched together but tracked apart: the card path needs
  // only properties, so a slow or failing templates request must not gate it.
  @observable.ref private templatesState: RemoteData<ResourceTemplateDto[]> = {
    _tag: 'NotAsked',
  }
  @observable.ref private propertiesState: RemoteData<ResourcePropertiesData> =
    {
      _tag: 'NotAsked',
    }

  @observable.ref private recordProjectId: string | null = null
  @observable.ref private schemaProjectId: string | null = null
  private schemaGeneration = 0
  private schemaLoad: SchemaLoad | null = null
  private pendingWorkflowRestrictionProjectId: string | null = null
  private pendingWorkflowRestrictions: IWorkflowRestrictionDelta[] = []

  /** Commands for control mutations owned by this resource store. */
  public readonly controls: IResourceControlCommands

  public constructor(
    private readonly graph: Pick<GraphExecutorStore, 'query' | 'mutate'>,
    private readonly resourceType: ProjectResourceType<
      TResource,
      TDomainFailure
    >,
  ) {
    this.controls = new ProjectResourceControls(graph, {
      prepareReplacement: this.prepareWorkflowRestrictionReplacement,
    })
    makeObservable(this)
  }

  /** Loads schema once for the requested project, joining an active load. */
  public ensureSchema(
    projectId: string,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    if (this.schemaLoad?.projectId === projectId) {
      return this.schemaLoad.promise
    }
    if (
      this.schemaProjectId === projectId &&
      this.templatesState._tag === 'Ready' &&
      this.propertiesState._tag === 'Ready'
    ) {
      return Promise.resolve(resourceOk(undefined))
    }
    return this.startSchemaLoad(projectId)
  }

  /** Returns the current observable projection for a project. */
  public viewFor(projectId: string): ProjectResourceView {
    const collection =
      this.recordProjectId === projectId ? dataOf(this.recordState) : null
    const ownsSchema = this.schemaProjectId === projectId
    const templates = ownsSchema ? dataOf(this.templatesState) : null
    const propertiesData = ownsSchema ? dataOf(this.propertiesState) : null

    const templatesLoading =
      !ownsSchema || this.templatesState._tag === 'Loading'
    const templatesError =
      ownsSchema && this.templatesState._tag === 'Failed'
        ? this.templatesState.error
        : null
    const propertiesLoading =
      !ownsSchema || this.propertiesState._tag === 'Loading'
    const propertiesError =
      ownsSchema && this.propertiesState._tag === 'Failed'
        ? this.propertiesState.error
        : null

    return {
      records: collection?.records ?? EMPTY_RECORDS,
      recordsWithDeletedItems: collection?.allRecords ?? EMPTY_RECORDS,
      loading:
        this.recordProjectId !== projectId ||
        this.recordState._tag === 'Loading',
      error:
        this.recordProjectId === projectId && this.recordState._tag === 'Failed'
          ? this.recordState.error
          : null,
      isReady:
        this.recordProjectId === projectId && this.recordState._tag === 'Ready',
      schema: {
        templates: templates ?? EMPTY_TEMPLATES,
        properties: propertiesData?.properties ?? EMPTY_PROPERTIES,
        allProperties: propertiesData?.allProperties ?? EMPTY_PROPERTIES,
        // Combined, and deliberately conservative: consumers that need templates keep
        // exactly the semantics they had before the halves were split.
        loading: templatesLoading || propertiesLoading,
        error: templatesError ?? propertiesError,
        isReady:
          ownsSchema &&
          this.templatesState._tag === 'Ready' &&
          this.propertiesState._tag === 'Ready',
        propertiesLoading,
        propertiesError,
        propertiesReady: ownsSchema && this.propertiesState._tag === 'Ready',
      },
    }
  }

  /**
   * A project's properties, read apart from its records and templates. The same object until one
   * of its fields changes: a record delta or a refresh that keeps the list leaves it as it was.
   */
  public readonly propertiesStateFor = computedFn(
    (projectId: string): ResourcePropertiesState => {
      const ownsSchema = this.schemaProjectId === projectId
      const data = ownsSchema ? dataOf(this.propertiesState) : null
      return {
        properties: data?.properties ?? EMPTY_PROPERTIES,
        propertiesKnown: data !== null,
        propertiesError:
          ownsSchema && this.propertiesState._tag === 'Failed'
            ? this.propertiesState.error
            : null,
      }
    },
    { equals: comparer.shallow },
  )

  /** Replaces records from the shared project-location query. */
  @action.bound public receiveList(
    projectId: string,
    locations: readonly ILocationBase[],
  ): ResourceResult<void, TDomainFailure> {
    const decoded = this.resourceType.decodeResources(locations)
    this.recordProjectId = projectId
    if (decoded._tag === 'Err') {
      this.recordState = {
        _tag: 'Failed',
        data: null,
        error: messageOf(decoded.error),
      }
      return decoded
    }
    const resources =
      this.pendingWorkflowRestrictionProjectId === projectId &&
      this.resourceType.applyWorkflowRestriction
        ? this.pendingWorkflowRestrictions.reduce(
            this.resourceType.applyWorkflowRestriction,
            decoded.value,
          )
        : decoded.value
    this.pendingWorkflowRestrictionProjectId = null
    this.pendingWorkflowRestrictions = []
    this.recordState = {
      _tag: 'Ready',
      data: collectionOf(resources, resources.map(this.resourceType.toRecord)),
    }
    return resourceOk(undefined)
  }

  /** Records a project-location load failure without discarding retained data. */
  @action.bound public receiveFailure(projectId: string, cause: unknown): void {
    const previous =
      this.recordProjectId === projectId ? dataOf(this.recordState) : null
    this.recordProjectId = projectId
    this.recordState = {
      _tag: 'Failed',
      data: previous,
      error: messageOf(cause),
    }
  }

  /** Applies one delta from the shared project-location subscription. */
  @action.bound public receiveOne(
    projectId: string,
    id: string,
    location: ILocationBase | null,
  ): ResourceResult<void, TDomainFailure> {
    if (this.recordProjectId !== projectId) return resourceOk(undefined)
    const current = dataOf(this.recordState)
    if (!current) return resourceOk(undefined)

    const decoded = this.resourceType.decodeResources(
      location === null ? [] : [location],
    )
    if (decoded._tag === 'Err') {
      this.recordState = {
        _tag: 'Failed',
        data: current,
        error: messageOf(decoded.error),
      }
      return decoded
    }

    const index = current.allRecords.findIndex(record => record.id === id)
    const replacement = decoded.value[0]
    if (!replacement) {
      if (index === -1) return resourceOk(undefined)
      this.recordState = {
        _tag: 'Ready',
        data: collectionOf(
          current.resources.filter((_, itemIndex) => itemIndex !== index),
          current.allRecords.filter((_, itemIndex) => itemIndex !== index),
        ),
      }
      return resourceOk(undefined)
    }

    const record = this.resourceType.toRecord(replacement)
    this.recordState = {
      _tag: 'Ready',
      data:
        index === -1
          ? collectionOf(
              [...current.resources, replacement],
              [...current.allRecords, record],
            )
          : collectionOf(
              current.resources.map((existing, itemIndex) =>
                itemIndex === index ? replacement : existing,
              ),
              current.allRecords.map((existing, itemIndex) =>
                itemIndex === index ? record : existing,
              ),
            ),
    }
    return resourceOk(undefined)
  }

  /** Applies one delta from the shared workflow-restriction subscription. */
  @action.bound public receiveWorkflowRestriction(
    projectId: string,
    delta: IWorkflowRestrictionDelta,
  ): void {
    const apply = this.resourceType.applyWorkflowRestriction
    if (!apply) return
    if (this.recordProjectId !== null && this.recordProjectId !== projectId) {
      return
    }
    const current = dataOf(this.recordState)
    if (!current) {
      if (this.pendingWorkflowRestrictionProjectId !== projectId) {
        this.pendingWorkflowRestrictionProjectId = projectId
        this.pendingWorkflowRestrictions = []
      }
      this.pendingWorkflowRestrictions.push(delta)
      return
    }

    const resources = apply(current.resources, delta)
    if (resources === current.resources) return

    this.recordState = {
      _tag: 'Ready',
      data: collectionOf(resources, resources.map(this.resourceType.toRecord)),
    }
  }

  private readonly deleteWorkflowRestrictionsByIds = (
    projectId: string,
    ids: readonly string[],
  ): void => {
    for (const id of ids) {
      this.receiveWorkflowRestriction(projectId, { id, item: null })
    }
  }

  private readonly prepareWorkflowRestrictionReplacement = (
    projectId: string,
    recordIds: readonly string[],
  ): ((successfulRecordIds: readonly string[]) => void) => {
    const current =
      this.recordProjectId === projectId ? dataOf(this.recordState) : null
    const requestedIds = new Set(recordIds)
    const previousByRecord = new Map(
      (current?.records ?? []).flatMap(record =>
        requestedIds.has(record.id)
          ? [[record.id, workflowRestrictionReferencesOf(record)] as const]
          : [],
      ),
    )

    return successfulRecordIds => {
      if (this.recordProjectId !== projectId) return
      const latest = dataOf(this.recordState)
      if (!latest) return
      const successfulIds = new Set(successfulRecordIds)
      const latestByRecord = new Map(
        latest.records.map(record => [
          record.id,
          new Map(
            workflowRestrictionReferencesOf(record).map(reference => [
              reference.id,
              reference.item,
            ]),
          ),
        ]),
      )
      const deletedIds = [...previousByRecord].flatMap(
        ([recordId, references]) => {
          if (!successfulIds.has(recordId)) return []
          const latestReferences = latestByRecord.get(recordId)
          return references.flatMap(reference =>
            latestReferences?.get(reference.id) === reference.item
              ? [reference.id]
              : [],
          )
        },
      )
      this.deleteWorkflowRestrictionsByIds(projectId, deletedIds)
    }
  }

  /** Reloads templates and properties for the configured resource type. */
  public refreshSchema(
    projectId: string,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    return this.startSchemaLoad(projectId)
  }

  private startSchemaLoad(
    projectId: string,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    const generation = this.beginSchemaLoad(projectId)
    const promise = this.loadSchema(projectId, generation).finally(() => {
      if (this.schemaLoad?.generation === generation) {
        this.schemaLoad = null
      }
    })
    this.schemaLoad = { projectId, generation, promise }
    return promise
  }

  private async loadSchema(
    projectId: string,
    generation: number,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    const variables = {
      projectId,
      resourceTypeId: this.resourceType.typeId,
    }

    // Both queries go out together, but each commits the moment it lands, so properties
    // are usable without waiting on templates — and one failing no longer discards the
    // other's data. loadSchema still resolves only once both settle, and still reports
    // Err if either failed, so the command contracts built on it are unchanged.
    const templatesLoad = this.graph
      .query(GetResourceTemplatesDocument, variables)
      .then(result => {
        if (result.error) {
          this.receiveTemplatesFailure(projectId, generation, result.error)
          return result.error
        }
        this.receiveTemplates(
          projectId,
          generation,
          result.data?.resourceTemplates ?? [],
        )
        return null
      })
      .catch((cause: unknown) => {
        this.receiveTemplatesFailure(projectId, generation, cause)
        return cause
      })

    const propertiesLoad = this.graph
      .query(GetResourcePropertiesDocument, {
        ...variables,
        includeArchived: true,
      })
      .then(result => {
        if (result.error) {
          this.receivePropertiesFailure(projectId, generation, result.error)
          return result.error
        }
        const allProperties = result.data?.resourceProperties ?? []
        this.receiveProperties(projectId, generation, {
          properties: allProperties.filter(property => !property.isArchived),
          allProperties,
        })
        return null
      })
      .catch((cause: unknown) => {
        this.receivePropertiesFailure(projectId, generation, cause)
        return cause
      })

    const [templatesCause, propertiesCause] = await Promise.all([
      templatesLoad,
      propertiesLoad,
    ])

    const cause = templatesCause ?? propertiesCause
    return cause ? this.failure('LoadSchema', cause) : resourceOk(undefined)
  }

  /**
   * Saves one resource and returns its id, which a new one only gets from the server; the
   * project-location subscription applies the result.
   */
  public async save(
    projectId: string,
    command: ResourceSaveCommand,
  ): Promise<
    ResourceResult<string | null, ProjectResourceFailure<TDomainFailure>>
  > {
    const variables = this.resourceType.buildSaveVariables(
      projectId,
      command,
      this.resourcesFor(projectId),
    )
    if (variables._tag === 'Err') return variables

    try {
      const result = await this.graph.mutate(
        this.resourceType.saveDocument,
        variables.value,
      )
      return result.error
        ? this.failure('Save', result.error)
        : resourceOk(savedResourceId(result.data))
    } catch (cause: unknown) {
      return this.failure('Save', cause)
    }
  }

  /** Applies property changes; the project-location subscription applies them. */
  public async bulkEdit(
    projectId: string,
    input: {
      readonly resourceIds: readonly string[]
      readonly changes: Readonly<Record<string, unknown>>
    },
  ): Promise<ResourceResult<void, ProjectResourceFailure<TDomainFailure>>> {
    if (input.resourceIds.length === 0) return resourceOk(undefined)
    return this.mutate('BulkEdit', BulkEditResourcePropertiesDocument, {
      projectId,
      input: {
        resourceTypeId: this.resourceType.typeId,
        resourceIds: [...input.resourceIds],
        changes: Object.fromEntries(
          Object.entries(input.changes).map(([key, value]) => [
            key,
            value === '' ? null : value,
          ]),
        ),
      },
    })
  }

  /** Deletes resources; the project-location subscription removes them. */
  public async deleteMany(
    ids: readonly string[],
  ): Promise<ResourceResult<void, ProjectResourceFailure<TDomainFailure>>> {
    if (ids.length === 0) return resourceOk(undefined)
    return this.mutate('Delete', DeleteLocationsDocument, {
      ids: [...ids],
    })
  }

  public async createForm(
    projectId: string,
    input: CreateResourceFormInput,
  ): Promise<
    ResourceResult<CreateResourceFormOutcome, ProjectResourceOperationFailure>
  > {
    const schemaContext = this.schemaCommandContext()
    const result = await this.executeMutation(
      'CreateForm',
      CreateResourceTemplateDocument,
      {
        projectId,
        input: {
          resourceTypeId: this.resourceType.typeId,
          name: input.name,
          description: input.description,
          codePrefix: input.codePrefix,
          properties: [...input.properties],
        },
      },
    )
    if (result._tag === 'Err') return result

    const formId = result.value.createResourceTemplate?.id
    if (!formId) {
      return this.failure('CreateForm', { _tag: 'MissingCreatedFormId' })
    }
    const synchronized = await this.synchronizeSchemaIfCurrent(
      projectId,
      schemaContext,
    )
    return resourceOk({
      _tag: synchronized ? 'Synchronized' : 'Committed',
      formId,
    })
  }

  public async updateForm(
    projectId: string,
    id: string,
    input: UpdateResourceFormInput,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    const schemaContext = this.schemaCommandContext()
    const updated = await this.mutate(
      'UpdateForm',
      UpdateResourceTemplateDocument,
      {
        projectId,
        id,
        input: {
          name: input.name,
          properties: input.properties ? [...input.properties] : undefined,
        },
      },
    )
    if (updated._tag === 'Err') return updated
    await this.synchronizeSchemaIfCurrent(projectId, schemaContext)
    return resourceOk(undefined)
  }

  public async deleteForms(
    projectId: string,
    ids: readonly string[],
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    if (ids.length === 0) return resourceOk(undefined)
    const schemaContext = this.schemaCommandContext()
    const deleted = await this.mutate(
      'DeleteForms',
      DeleteManyResourceTemplatesDocument,
      { projectId, ids: [...ids] },
    )
    if (deleted._tag === 'Err') return deleted
    await this.synchronizeSchemaIfCurrent(projectId, schemaContext)
    return resourceOk(undefined)
  }

  public async createProperty(
    projectId: string,
    input: CreateResourcePropertyInput,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    const loaded = await this.ensureSchema(projectId)
    if (loaded._tag === 'Err') return loaded
    const templates = this.schemaFor(projectId)?.templates ?? []
    const schemaContext = this.schemaCommandContext()
    const created = await this.executeMutation(
      'CreateProperty',
      CreateResourcePropertyDocument,
      {
        projectId,
        input: {
          resourceTypeId: this.resourceType.typeId,
          name: input.name,
          displayName: input.displayName,
          propertyType: input.propertyType,
          configuration: input.configuration,
        },
      },
    )
    if (created._tag === 'Err') return created

    const propertyId = created.value.createResourceProperty?.id
    if (!propertyId) {
      return this.failure('CreateProperty', {
        _tag: 'MissingCreatedPropertyId',
      })
    }
    if (templates.length === 1) {
      const attached = await this.addPropertyToOnlyForm(
        projectId,
        templates[0],
        propertyId,
      )
      if (attached._tag === 'Err') return attached
    }
    await this.synchronizeSchemaIfCurrent(projectId, schemaContext)
    return resourceOk(undefined)
  }

  public async updateProperty(
    projectId: string,
    id: string,
    input: UpdateResourcePropertyInput,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    return this.mutatePropertyThenRefresh(
      'UpdateProperty',
      UpdateResourcePropertyDocument,
      projectId,
      id,
      input,
    )
  }

  public async archiveProperty(
    projectId: string,
    id: string,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    return this.mutatePropertyThenRefresh(
      'ArchiveProperty',
      ArchiveResourcePropertyDocument,
      projectId,
      id,
    )
  }

  public async unarchiveProperty(
    projectId: string,
    id: string,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    return this.mutatePropertyThenRefresh(
      'UnarchiveProperty',
      UnarchiveResourcePropertyDocument,
      projectId,
      id,
    )
  }

  public async deleteProperty(
    projectId: string,
    id: string,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    return this.mutatePropertyThenRefresh(
      'DeleteProperty',
      DeleteResourcePropertyDocument,
      projectId,
      id,
    )
  }

  /** Removes every retained project resource and schema value. */
  @action.bound public clear(): void {
    this.recordProjectId = null
    this.schemaProjectId = null
    this.schemaGeneration += 1
    this.schemaLoad = null
    this.pendingWorkflowRestrictionProjectId = null
    this.pendingWorkflowRestrictions = []
    this.recordState = { _tag: 'NotAsked' }
    this.templatesState = { _tag: 'NotAsked' }
    this.propertiesState = { _tag: 'NotAsked' }
  }

  @action.bound private beginSchemaLoad(projectId: string): number {
    this.schemaGeneration += 1
    const ownsSchema = this.schemaProjectId === projectId
    const previousTemplates = ownsSchema ? dataOf(this.templatesState) : null
    const previousProperties = ownsSchema ? dataOf(this.propertiesState) : null
    this.schemaProjectId = projectId
    this.templatesState = { _tag: 'Loading', data: previousTemplates }
    this.propertiesState = { _tag: 'Loading', data: previousProperties }
    return this.schemaGeneration
  }

  /** True while the given load still owns the schema slot. */
  private isCurrentSchemaLoad(projectId: string, generation: number): boolean {
    return (
      this.schemaProjectId === projectId && this.schemaGeneration === generation
    )
  }

  @action.bound private receiveTemplates(
    projectId: string,
    generation: number,
    templates: ResourceTemplateDto[],
  ): void {
    if (!this.isCurrentSchemaLoad(projectId, generation)) return
    this.templatesState = { _tag: 'Ready', data: templates }
  }

  @action.bound private receiveTemplatesFailure(
    projectId: string,
    generation: number,
    cause: unknown,
  ): void {
    if (!this.isCurrentSchemaLoad(projectId, generation)) return
    this.templatesState = {
      _tag: 'Failed',
      data: dataOf(this.templatesState),
      error: messageOf(cause),
    }
  }

  @action.bound private receiveProperties(
    projectId: string,
    generation: number,
    properties: ResourcePropertiesData,
  ): void {
    if (!this.isCurrentSchemaLoad(projectId, generation)) return
    this.propertiesState = { _tag: 'Ready', data: properties }
  }

  @action.bound private receivePropertiesFailure(
    projectId: string,
    generation: number,
    cause: unknown,
  ): void {
    if (!this.isCurrentSchemaLoad(projectId, generation)) return
    this.propertiesState = {
      _tag: 'Failed',
      data: dataOf(this.propertiesState),
      error: messageOf(cause),
    }
  }

  // Live resources only: this feeds save payloads, and a deleted record must
  // still fail the type module's missing-record guard rather than be rewritten.
  private resourcesFor(projectId: string): readonly TResource[] {
    if (this.recordProjectId !== projectId) return []
    const collection = dataOf(this.recordState)
    if (!collection) return []
    return collection.resources.filter(
      (_, index) => !collection.allRecords[index]?.isDeleted,
    )
  }

  private schemaFor(projectId: string): ResourceSchemaData | null {
    if (this.schemaProjectId !== projectId) return null
    const templates = dataOf(this.templatesState)
    const properties = dataOf(this.propertiesState)
    if (!templates || !properties) return null

    return {
      templates,
      properties: properties.properties,
      allProperties: properties.allProperties,
    }
  }

  private addPropertyToOnlyForm(
    projectId: string,
    form: ResourceTemplateDto,
    propertyId: string,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    const properties = (form.properties ?? []).map(property => ({
      propertyId: property.propertyId,
      orderIndex: property.orderIndex,
      isRequired: property.isRequired,
      isHidden: property.isHidden,
      defaultValue: property.defaultValue,
    }))
    return this.mutate('UpdateForm', UpdateResourceTemplateDocument, {
      projectId,
      id: form.id,
      input: {
        properties: [
          ...properties,
          { propertyId, orderIndex: properties.length },
        ],
      },
    })
  }

  private async mutatePropertyThenRefresh(
    operation: ProjectResourceOperation,
    mutation: DocumentNode,
    projectId: string,
    id: string,
    input?: UpdateResourcePropertyInput,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    const schemaContext = this.schemaCommandContext()
    const mutated = await this.mutate(operation, mutation, {
      projectId,
      id,
      ...(input ? { input } : {}),
    })
    if (mutated._tag === 'Err') return mutated
    await this.synchronizeSchemaIfCurrent(projectId, schemaContext)
    return resourceOk(undefined)
  }

  private schemaCommandContext(): SchemaCommandContext {
    return {
      ownerProjectId: this.schemaProjectId,
      generation: this.schemaGeneration,
    }
  }

  private async synchronizeSchemaIfCurrent(
    projectId: string,
    context: SchemaCommandContext,
  ): Promise<boolean> {
    const remainedUnowned =
      context.ownerProjectId === null &&
      this.schemaProjectId === null &&
      this.schemaGeneration === context.generation
    if (this.schemaProjectId !== projectId && !remainedUnowned) return false

    const refreshed = await this.refreshSchema(projectId)
    return refreshed._tag === 'Ok'
  }

  private async executeMutation(
    operation: ProjectResourceOperation,
    mutation: DocumentNode,
    variables: Readonly<Record<string, unknown>>,
  ): Promise<
    ResourceResult<
      {
        readonly createResourceTemplate?: { readonly id?: string | null } | null
        readonly createResourceProperty?: { readonly id?: string | null } | null
      },
      ProjectResourceOperationFailure
    >
  > {
    try {
      const result = await this.graph.mutate(mutation, variables)
      if (result.error) return this.failure(operation, result.error)
      return resourceOk(result.data ?? {})
    } catch (cause: unknown) {
      return this.failure(operation, cause)
    }
  }

  private async mutate(
    operation: ProjectResourceOperation,
    mutation: DocumentNode,
    variables: Readonly<Record<string, unknown>>,
  ): Promise<ResourceResult<void, ProjectResourceOperationFailure>> {
    try {
      const result = await this.graph.mutate(mutation, variables)
      return result.error
        ? this.failure(operation, result.error)
        : resourceOk(undefined)
    } catch (cause: unknown) {
      return this.failure(operation, cause)
    }
  }

  private failure(
    operation: ProjectResourceOperation,
    cause: unknown,
  ): ResourceResult<never, ProjectResourceOperationFailure> {
    return resourceErr({
      _tag: 'ProjectResourceOperationFailure',
      operation,
      cause,
    })
  }
}
